import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, CalendarDays, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, Circle } from "lucide-react";
import { format, subDays, startOfMonth, startOfDay, endOfDay, startOfWeek, endOfWeek } from "date-fns";
import { ActivityTracker, type DateRange } from "@/components/activity-tracker";
import { ManagerGamificationTile } from "@/components/gamification/manager-tile";
import { TimecardManagerPanel } from "@/components/timecard/timecard-manager-panel";

interface UsersResponse {
  users: Array<{ userId: string; userName: string; isOnline?: boolean }>;
}

export default function TeamActivityPage() {
  const [dateRange, setDateRange] = useState<DateRange>("7d");
  const [customStart, setCustomStart] = useState<Date | undefined>();
  const [customEnd, setCustomEnd] = useState<Date | undefined>();
  const [showComparison, setShowComparison] = useState(true);
  const [userFilter, setUserFilter] = useState<string>("all");

  const { start, end } = useMemo(() => {
    const now = new Date();
    switch (dateRange) {
      case "today":
        return { start: startOfDay(now), end: endOfDay(now) };
      case "yesterday": {
        const y = subDays(now, 1);
        return { start: startOfDay(y), end: endOfDay(y) };
      }
      case "this-week":
        return { start: startOfWeek(now, { weekStartsOn: 0 }), end: endOfWeek(now, { weekStartsOn: 0 }) };
      case "7d":
        return { start: startOfDay(subDays(now, 7)), end: endOfDay(now) };
      case "this-month":
        return { start: startOfMonth(now), end: endOfDay(now) };
      case "30d":
        return { start: startOfDay(subDays(now, 30)), end: endOfDay(now) };
      case "custom":
        return {
          start: customStart || startOfDay(subDays(now, 7)),
          end: customEnd || endOfDay(now),
        };
      default:
        return { start: startOfDay(subDays(now, 7)), end: endOfDay(now) };
    }
  }, [dateRange, customStart, customEnd]);

  const userIdParam = userFilter !== "all" ? userFilter : undefined;

  const allUsersQuery = useQuery<UsersResponse>({
    queryKey: ["/api/admin/activity-tracker/users", start.toISOString(), end.toISOString(), "userName", "asc", "all"],
    queryFn: async () => {
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
        sort: "userName",
        dir: "asc",
      });
      const response = await fetch(`/api/admin/activity-tracker/users?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch users");
      return response.json();
    },
  });

  const allUsers = allUsersQuery.data?.users || [];
  const onlineCount = allUsers.filter((u) => u.isOnline).length;

  const handleExportCsv = () => {
    const params = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
    if (userIdParam) params.set("userId", userIdParam);
    window.location.href = `/api/admin/activity-tracker/export.csv?${params}`;
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-primary" />
          <h1 className="text-xl font-semibold">Team Activity</h1>
          {onlineCount > 0 && (
            <Badge
              variant="outline"
              className="bg-green-500/10 text-green-600 border-green-500/30 gap-1.5"
              data-testid="badge-online-count"
            >
              <Circle className="w-2 h-2 fill-green-500 text-green-500" />
              {onlineCount} online
            </Badge>
          )}
        </div>
      </div>

      <Tabs defaultValue="activity">
        <TabsList data-testid="tabs-team-activity">
          <TabsTrigger value="activity" data-testid="tab-activity">
            <Activity className="w-3.5 h-3.5 mr-1.5" />
            Activity
          </TabsTrigger>
          <TabsTrigger value="timecards" data-testid="tab-timecards">
            <Clock className="w-3.5 h-3.5 mr-1.5" />
            Timecards
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activity" className="mt-4 space-y-4">
          {/* Filter bar */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="team-activity-filter-bar">
            {/* Date-range presets */}
            <div className="flex items-center gap-1 flex-wrap" data-testid="bar-date-presets">
              {([
                { v: "today", label: "Today" },
                { v: "yesterday", label: "Yesterday" },
                { v: "this-week", label: "This Week" },
                { v: "7d", label: "Last 7 Days" },
                { v: "this-month", label: "This Month" },
                { v: "30d", label: "Last 30 Days" },
                { v: "custom", label: "Custom" },
              ] as Array<{ v: DateRange; label: string }>).map(({ v, label }) => (
                <Button
                  key={v}
                  size="sm"
                  variant={dateRange === v ? "default" : "outline"}
                  onClick={() => setDateRange(v)}
                  data-testid={`button-preset-${v}`}
                >
                  {label}
                </Button>
              ))}
            </div>

            {dateRange === "custom" && (
              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="button-custom-start">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      {customStart ? format(customStart, "MMM d") : "Start"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={customStart} onSelect={setCustomStart} />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground">to</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="button-custom-end">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      {customEnd ? format(customEnd, "MMM d") : "End"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* User filter */}
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-44" data-testid="select-user-filter">
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {allUsers.map((u) => (
                  <SelectItem key={u.userId} value={u.userId} data-testid={`option-user-${u.userId}`}>
                    {u.userName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant={showComparison ? "default" : "outline"}
              size="sm"
              onClick={() => setShowComparison(!showComparison)}
              aria-pressed={showComparison}
              data-testid="button-toggle-comparison"
            >
              Compare Period
            </Button>

            <span
              role="status"
              aria-live="polite"
              className="sr-only"
              data-testid="status-comparison-toggle"
            >
              {showComparison
                ? "Period comparison enabled. Metric cards now show change vs the previous period."
                : "Period comparison disabled. Metric cards now show absolute values only."}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              data-testid="button-export-csv"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Goals tile — always shows the full team regardless of the user filter */}
          <ManagerGamificationTile dateRange={dateRange} userFilter="all" />

          {/* Activity Tracker body — filter bar hidden since we own it above */}
          <ActivityTracker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customStart={customStart}
            onCustomStartChange={setCustomStart}
            customEnd={customEnd}
            onCustomEndChange={setCustomEnd}
            showComparison={showComparison}
            onShowComparisonChange={setShowComparison}
            userFilter={userFilter}
            onUserFilterChange={setUserFilter}
            start={start}
            end={end}
            hideFilterBar
          />
        </TabsContent>

        <TabsContent value="timecards" className="mt-4">
          <TimecardManagerPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
